import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { EAgentKeys, EWebScrapeStatus, TAgent } from '~/features/agents/models'
import { getDefaultAgentData, queryKeys } from '~/features/agents/utils'
import { EServerPath } from '~/models/api'
import HttpService from '~/services/http'
import { HttpServiceBaseResponse } from '~/services/http/models'
import pathBuilder from '~/utils/api'

type TPartialAgentUpdate = Partial<Omit<TAgent, 'id'>>

type TUpdateAgentData = {
  agentId: string
  payload: TPartialAgentUpdate
  hideToast?: boolean
}

const useUpdateAgent = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ agentId, payload }: TUpdateAgentData) => {
      return HttpService.put<TAgent>({
        path: pathBuilder(EServerPath.agent, { agentId }),
        payload,
      })
    },
    onSuccess: (response, variables) => {
      const { agentId, hideToast } = variables
      if (!hideToast) {
        toast.dismiss()
        toast.success('Agent has been updated!')
      }

      if (response?.data) {
        const newData = {
          ...response,
          data: getDefaultAgentData({
            ...(response.data || {}),
            scrapeStatus: variables.payload.scrapeStatus
              ? variables.payload.scrapeStatus
              : response.data?.[EAgentKeys.scrapeStatus] ||
                EWebScrapeStatus.unloaded,
          }),
        }
        queryClient.setQueryData(queryKeys.agent(response?.data?.id), newData)

        const agentsByOrganizationData = queryClient.getQueryData(
          queryKeys.agentsByOrganization(response?.data?.orgId || ''),
        ) as HttpServiceBaseResponse<TAgent[]> | undefined

        if (agentsByOrganizationData) {
          queryClient.setQueryData(
            queryKeys.agentsByOrganization(response?.data?.orgId || ''),
            {
              ...agentsByOrganizationData,
              data: agentsByOrganizationData?.data?.map((agent: TAgent) => {
                if (agent.id === agentId) {
                  return newData.data || agent
                }

                return agent
              }),
            },
          )
        }
      }

      queryClient.invalidateQueries({
        queryKey: queryKeys.agents({}),
        exact: false,
      })

      queryClient.invalidateQueries({
        queryKey: queryKeys.agentsByOrganization(response.data?.orgId || ''),
      })

      queryClient.invalidateQueries({
        queryKey: queryKeys.adminAgents({}),
      })

      if (variables.payload.phoneNumber) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.agentPhoneNumber(agentId),
        })
      }

      if (variables.payload.agentEmail) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.agentSignature(agentId),
        })
      }
    },
    onError: (_, variables) => {
      toast.dismiss()
      toast.error("Couldn't update Agent, please try again later.")
      queryClient.invalidateQueries({
        queryKey: queryKeys.agent(variables.agentId),
      })
    },
    retry: false,
  })
}

export default useUpdateAgent
